/**
 * Core-100 R4 — CORE-AD: "Reflect state promptly (a stale board is a hazard)."
 *
 * The Dayboard already refetches when its realtime channel fails, but nothing told the person the board might be
 * out of date while it reconnected. This banner says so — with the time the board was last known live — and clears
 * itself when the channel is subscribed again.
 *
 * Core-100 R4 — CORE-X ("a stale picture is worse than no picture"): Birdseye uses the same banner for the floor map.
 * Its channel stops re-subscribing after a bounded number of failures (rebuildDelayMs), so the banner can also say
 * that it is NOT reconnecting on its own, show when the map last loaded, and offer a Refresh.
 */
import { RefreshCw, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

export type LiveState = { live: true } | { live: false; since: Date };

/** The live state after a channel status — pure, so the transitions are tested directly. */
export function nextLiveState(prev: LiveState, status: string, abnormal: boolean, now: Date): LiveState {
  if (status === 'SUBSCRIBED') return { live: true };
  if (abnormal) return prev.live ? { live: false, since: now } : prev; // keep the FIRST moment it was lost
  return prev;
}

export interface LiveUpdatesBannerProps {
  state: LiveState;
  timeZone?: string;
  /** What may be out of date (default "the board"). */
  subject?: string;
  /** False once automatic re-subscription has given up. Default true. */
  reconnecting?: boolean;
  /** When the picture was last loaded from the server. */
  lastRefreshedAt?: Date | null;
  /** A manual reload + resubscribe. */
  onRefresh?: () => void;
}

export function LiveUpdatesBanner({
  state, timeZone, subject = 'the board', reconnecting = true, lastRefreshedAt = null, onRefresh,
}: LiveUpdatesBannerProps) {
  if (state.live !== false) return null;
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  const at = fmt(state.since);
  return (
    <div role="status" data-testid="live-updates-banner" data-reconnecting={reconnecting ? 'true' : 'false'}
      className="mx-4 mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden />
      <span>
        Live updates paused — {subject} may be out of date since {at}.{' '}
        {reconnecting ? 'Reconnecting…' : 'Not reconnecting on its own.'}
        {lastRefreshedAt ? <> Last refreshed {fmt(lastRefreshedAt)}.</> : null}
      </span>
      {onRefresh && (
        <Button size="sm" variant="outline" className="h-7" onClick={onRefresh} data-testid="live-updates-refresh">
          Refresh
        </Button>
      )}
    </div>
  );
}

/**
 * R3-A OPTION B (founder requirement 4: "Reconcile immediately whenever Dayboard opens or reconnects, avoiding
 * presentation of stale scheduling data as current"). Run Long AutoBump runs on the server; the board asks it to
 * reconcile on open / reconnect. While that reconcile is pending the board is "Updating schedule…", not live; when
 * the shop's last completed reconcile is older than 3 minutes it says when that was, never "live". The realtime
 * banner above keeps its own job (and wins while the channel is down).
 */
export interface ScheduleReconcileState {
  pending: boolean;
  lastReconciledAt: Date | null;
  stale: boolean;
  /** oct20c: the server's last reconcile did not fully succeed — it is retrying. Never shown as fresh / live. */
  failing?: boolean;
}

/** What the reconcile banner says — pure, so the states are tested directly. */
export function reconcileBannerKind(r: ScheduleReconcileState | null | undefined, live: LiveState): 'updating' | 'failing' | 'stale' | null {
  if (!r || live.live === false) return null;
  if (r.pending) return 'updating';
  if (r.failing) return 'failing';
  if (r.stale) return 'stale';
  return null;
}

export function ScheduleReconcileBanner({ reconcile, live, timeZone }: {
  reconcile: ScheduleReconcileState | null | undefined; live: LiveState; timeZone?: string;
}) {
  const kind = reconcileBannerKind(reconcile, live);
  if (!kind) return null;
  const fmt = (d: Date) => d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) });
  return (
    <div role="status" data-testid="schedule-reconcile-banner" data-state={kind}
      className="mx-4 mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
      <RefreshCw className={`h-4 w-4 shrink-0${kind === 'updating' ? ' animate-spin' : ''}`} aria-hidden />
      <span>
        {kind === 'updating'
          ? 'Updating schedule… the times shown may be behind until the server finishes reconciling.'
          : kind === 'failing'
            ? <>Schedule could not be fully updated — retrying.{reconcile?.lastReconciledAt
                ? <> Last fully updated {fmt(reconcile.lastReconciledAt)}; some times may be behind.</>
                : ' Some times may be behind.'}</>
          : reconcile?.lastReconciledAt
            ? <>Schedule last reconciled {fmt(reconcile.lastReconciledAt)} — times may be behind.</>
            : 'Schedule not yet reconciled — times may be behind.'}
      </span>
    </div>
  );
}
